/**
 * Main entry point for the domino game client
 * Handles initialization and screen management with lobby integration
 */

import { WebSocketConnection } from './websocket';
import { GameUI } from './game';
import { LobbyUI } from './lobby';
import { ServerMessage, ClientLobbyState, UserIdentity } from './types';
import { getUserByNickname } from './api/netcartasApi';
import { PrivateChat } from './components/PrivateChat';
import { ChatService } from './services/ChatService';  // DATE: 2025-12-21 | Deep link support
import { FriendRequestPopup } from './components/FriendRequestPopup';  // DATE: 2025-12-22 | AI friend request popup

// Server configuration
const SERVER_URL = import.meta.env.DEV
  ? 'http://localhost:8881'
  : 'https://domino-server.pixlabs.workers.dev';

// ============================================================================
// DATE: 2025-12-14 | Author: IA + Luiz Couto
// Auto-login via URL parameters or cookies (NetCartas integration)
// Follows the same pattern as buraco-html5/js/parameters.js
// ============================================================================

/**
 * Gets a cookie value by name
 * @param name - The cookie name to retrieve
 * @returns The cookie value or null if not found
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Gets authentication data from URL parameters or cookies
 * URL parameters take precedence over cookies
 *
 * DATE: 2025-12-17 | Author: IA + Luiz Couto
 * Extended to also extract userId from URL param 'i' for friend system integration
 *
 * @returns Object with username and optional userId if found, null otherwise
 */
function getAuthFromUrlOrCookie(): { username: string; userId: number | null } | null {
  const urlParams = new URLSearchParams(window.location.search);

  // Try to get username from URL parameters first (NetCartas pattern: u=username)
  let username = urlParams.get('u');

  // DATE: 2025-12-17 | Author: IA + Luiz Couto
  // Also extract userId from URL param 'i' (NetCartas integration)
  const userIdParam = urlParams.get('i');
  const userId = userIdParam ? parseInt(userIdParam, 10) : null;

  if (username) {
    console.info('[2025-12-17] Found auth data in URL - username:', username, 'userId:', userId);
  } else {
    // Fallback to cookie (NetCartas pattern)
    username = getCookie('cookie_NC_LOGIN_USERNAME');
    if (username) {
      console.info('[2025-12-17] Found auth data in cookie - username:', username);
    }
  }

  if (username && username.trim() !== '') {
    return {
      username: username.trim(),
      userId: userId && !isNaN(userId) ? userId : null
    };
  }

  return null;
}

/**
 * DATE: 2025-12-21 | Author: IA + Luiz Couto
 * Converts a NetCartas profile photo path to a full URL
 * The API returns relative paths like "users/profile/photo_profile_123.jpg"
 * This function adds the required prefix: https://images.netcartas.com.br/
 *
 * @param profilePhoto - Relative path from NetCartas API (or undefined/null)
 * @returns Full URL or undefined if no photo
 */
function getNetCartasImageUrl(profilePhoto: string | undefined | null): string | undefined {
  if (!profilePhoto) return undefined;

  // If already has http/https prefix, return as-is
  if (profilePhoto.startsWith('http://') || profilePhoto.startsWith('https://')) {
    return profilePhoto;
  }

  // Add NetCartas images server prefix
  const NETCARTAS_IMAGES_BASE = 'https://images.netcartas.com.br/';
  return `${NETCARTAS_IMAGES_BASE}${profilePhoto}`;
}

/**
 * Cleans sensitive parameters from URL using history.pushState
 * Removes auth parameters (u, p, i, m) while preserving other params
 * DATE: 2025-12-21 | Preserves openChat param for deep link support
 */
function cleanUrlParameters(): void {
  const urlParams = new URLSearchParams(window.location.search);

  // Check if there are sensitive params to remove
  const hasAuthParams = urlParams.has('u') || urlParams.has('p') || urlParams.has('i') || urlParams.has('m');

  if (!hasAuthParams) {
    return; // Nothing to clean
  }

  // Remove sensitive auth parameters (but keep openChat for deep link)
  urlParams.delete('u');
  urlParams.delete('p');
  urlParams.delete('i');
  urlParams.delete('m');

  // Build new URL
  const newSearch = urlParams.toString();
  const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;

  // Update browser URL without reloading
  history.pushState({ cleaned: true }, '', newUrl);
  console.info('[2025-12-14] URL cleaned - sensitive parameters removed');
}

/**
 * Get openChat parameter from URL for deep link support
 * DATE: 2025-12-21 | Author: IA + Luiz Couto
 * Used to automatically open chat with a specific AI Companion after login
 *
 * @returns Companion name if found, null otherwise
 */
function getOpenChatParam(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const openChat = urlParams.get('openChat');
  if (openChat) {
    console.info(`🔗 [2025-12-21] Deep link detected: openChat=${openChat}`);
  }
  return openChat;
}

/**
 * DATE: 2025-12-26 | Author: IA + Luiz Couto
 * Get game deep link parameters from URL
 * Used for direct navigation to a game room from NetCartas chat invite
 *
 * URL format: /game/{gameId}?type={gameType}&u={username}&i={userId}&ai={aiNetcartasId}
 * Example: /game/private-ai-GaKfAR7y?type=individual_2p&u=joao&i=12345&ai=900001
 *
 * @returns Object with gameId, gameType and optional auth/ai params if present, null otherwise
 */
function getGameDeepLinkParams(): { gameId: string; gameType: string; username?: string; userId?: number; aiNetcartasId?: number } | null {
  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);

  // Check if URL matches /game/{gameId} pattern
  const gameMatch = pathname.match(/^\/game\/([^/?]+)/);

  if (gameMatch) {
    const gameId = gameMatch[1];
    const gameType = urlParams.get('type') || 'individual_2p';

    // Also extract auth params if present (for auto-login)
    const username = urlParams.get('u') || undefined;
    const userIdParam = urlParams.get('i');
    const userId = userIdParam ? parseInt(userIdParam, 10) : undefined;

    // DATE: 2025-12-26 | Extract AI netcartas ID for proper AI companion selection
    const aiParam = urlParams.get('ai');
    const aiNetcartasId = aiParam ? parseInt(aiParam, 10) : undefined;

    console.info(`🎮 [2025-12-26] Game deep link detected: gameId=${gameId}, gameType=${gameType}, username=${username}, userId=${userId}, aiNetcartasId=${aiNetcartasId}`);
    return {
      gameId,
      gameType,
      username,
      userId: userId && !isNaN(userId) ? userId : undefined,
      aiNetcartasId: aiNetcartasId && !isNaN(aiNetcartasId) ? aiNetcartasId : undefined
    };
  }

  return null;
}

class DominoGame {
  private gameWs: WebSocketConnection;
  private lobbyWs: WebSocketConnection;
  private gameUI: GameUI;
  private lobbyUI: LobbyUI;
  private currentRoomId: string | null = null;
  private playerId: string | null = null;
  private lobbyConnected: boolean = false;
  private isSpectator: boolean = false; // 2025-10-28: Track if viewing as spectator

  // DATE: 2025-12-17 | Author: IA + Luiz Couto
  // UserIdentity - Armazena dados completos do usuário para sistema de amizade
  private userIdentity: UserIdentity | null = null;

  // DATE: 2025-12-17 | Author: IA + Luiz Couto
  // PrivateChat - WhatsApp-style chat component for private messaging with friends
  private privateChat: PrivateChat;

  // DATE: 2025-12-22 | Author: IA + Luiz Couto
  // FriendRequestPopup - Popup for AI Companion friend requests during game
  private friendRequestPopup: FriendRequestPopup;

  // DATE: 2025-12-21 | Author: IA + Luiz Couto
  // Deep link support - stores openChat param to open chat after lobby loads
  private pendingOpenChat: string | null = null;

  // DATE: 2025-12-26 | Author: IA + Luiz Couto
  // Game deep link support - stores game params to connect after login
  // aiNetcartasId is used to select the correct AI companion to join the game
  private pendingGameDeepLink: { gameId: string; gameType: string; username?: string; userId?: number; aiNetcartasId?: number } | null = null;

  constructor() {
    // DATE: 2025-12-21 | Capture openChat param before any URL manipulation
    this.pendingOpenChat = getOpenChatParam();

    // DATE: 2025-12-26 | Capture game deep link params before any URL manipulation
    this.pendingGameDeepLink = getGameDeepLinkParams();

    this.gameWs = new WebSocketConnection();
    this.lobbyWs = new WebSocketConnection();
    this.gameUI = new GameUI(this.gameWs);
    this.lobbyUI = new LobbyUI(SERVER_URL); // DATE: 2025-10-31 | Author: IA + Luiz Couto - Pass server URL
    this.privateChat = new PrivateChat(); // DATE: 2025-12-17 | Author: IA + Luiz Couto - Private chat component
    this.friendRequestPopup = new FriendRequestPopup(); // DATE: 2025-12-22 | Author: IA + Luiz Couto - AI friend request popup
    this.initializeEventListeners();
    this.setupGameMessageHandlers();
    this.setupLobbyMessageHandlers();
    this.setupNavigationHandlers();
  }

  /**
   * Initialize UI event listeners for login screen
   */
  private initializeEventListeners(): void {
    // Enter lobby button (new login flow)
    document.getElementById('enter-lobby-btn')?.addEventListener('click', () => {
      this.enterLobby();
    });

    // Enter key on player name input
    document.getElementById('player-name')?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        document.getElementById('enter-lobby-btn')?.click();
      }
    });

    // Leave room button
    document.getElementById('leave-room-btn')?.addEventListener('click', () => {
      this.leaveRoom();
    });

    // Leave lobby button
    document.getElementById('leave-lobby-btn')?.addEventListener('click', () => {
      this.leaveLobby();
    });

    // Copy room ID button
    document.getElementById('copy-room-id')?.addEventListener('click', () => {
      if (this.currentRoomId) {
        navigator.clipboard.writeText(this.currentRoomId).then(() => {
          alert('ID da sala copiado!');
        });
      }
    });
  }

  /**
   * Setup navigation event handlers
   */
  private setupNavigationHandlers(): void {
    // Handle navigate to game event from lobby
    // 2025-10-28: Added support for spectator role
    // DATE: 2025-12-26 | Added aiNetcartasId for proper AI companion selection
    document.addEventListener('navigateToGame', (e: any) => {
      const gameId = e.detail.gameId;
      const gameType = e.detail.gameType;
      const role = e.detail.role || 'player'; // Default to player
      const aiNetcartasId = e.detail.aiNetcartasId; // Optional AI ID for correct AI selection
      this.connectToRoom(gameId, gameType, role, aiNetcartasId);
    });

    // Handle create room from lobby
    document.addEventListener('createRoom', async () => {
      await this.createRoom();
    });

    // Handle go back to lobby from game
    document.getElementById('back-to-lobby-btn')?.addEventListener('click', () => {
      this.backToLobby();
    });
  }

  /**
   * Setup WebSocket message handlers for game room
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * Extended to include netcartasId in join message for friend system
   */
  private setupGameMessageHandlers(): void {
    // Connection established
    this.gameWs.on('connected', (message: ServerMessage) => {
      if (message.type === 'connected') {
        this.playerId = message.playerId;
        this.gameUI.setPlayerId(message.playerId);

        // 2025-10-28: Send appropriate message based on role
        const nameInput = document.getElementById('player-name') as HTMLInputElement;
        const playerName = nameInput.value.trim() || `Jogador ${Math.floor(Math.random() * 1000)}`;

        if (this.isSpectator) {
          console.log('👁️ [2025-10-28] Sending spectate message to server');
          this.gameWs.send({ type: 'spectate', name: playerName });
        } else {
          // DATE: 2025-12-17 | Author: IA + Luiz Couto
          // Include netcartasId in join message for friend system
          // DATE: 2025-12-21 | Include profileImageURL for avatar display
          console.log('🎮 [2025-12-21] Joining game with netcartasId:', this.userIdentity?.netcartasId, 'profileImageURL:', this.userIdentity?.profileImageURL);
          this.gameWs.send({
            type: 'join',
            name: playerName,
            netcartasId: this.userIdentity?.netcartasId || null,
            profileImageURL: this.userIdentity?.profileImageURL || null
          });
        }
      }
    });

    // Game state update
    this.gameWs.on('gameState', (message: ServerMessage) => {
      if (message.type === 'gameState') {
        // DEBUG: Log players with netcartasId to verify propagation
        console.log('🔍 [2025-12-18 DEBUG] GameState players received:');
        if (message.state?.players) {
          Object.entries(message.state.players).forEach(([id, player]: [string, any]) => {
            console.log(`   Player ${id}: ${player.name}, netcartasId=${player.netcartasId}`);
          });
        }
        this.gameUI.updateGameState(message.state);
      }
    });

    // Player joined
    this.gameWs.on('playerJoined', (message: ServerMessage) => {
      if (message.type === 'playerJoined') {
        this.gameUI.addChatMessage('system', 'Sistema', `${message.player.name} entrou na sala`);
      }
    });

    // Player left
    this.gameWs.on('playerLeft', (message: ServerMessage) => {
      if (message.type === 'playerLeft') {
        this.gameUI.addChatMessage('system', 'Sistema', `Um jogador saiu da sala`);
      }
    });

    // Game started
    this.gameWs.on('gameStarted', (_message: ServerMessage) => {
      this.gameUI.addChatMessage('system', 'Sistema', 'O jogo começou!');
      this.gameUI.playGameStartSound();
    });

    // Tile played
    // Date: 2025-10-19 | Author: IA + Luiz Couto
    // Added tile animation when a player places a tile
    this.gameWs.on('tilePlayed', (message: ServerMessage) => {
      if (message.type === 'tilePlayed') {
        // Animate tile from player position to board
        this.gameUI.animateTileToBoard(message.playerId, message.tile);
        // Play tile placed sound
        this.gameUI.playTilePlacedSound();
        // Game state will be updated separately
      }
    });

    // Tile drawn
    this.gameWs.on('tileDrawn', (message: ServerMessage) => {
      if (message.type === 'tileDrawn') {
        if (message.tile && message.playerId === this.playerId) {
          // this.gameUI.addChatMessage('system', 'Sistema', `Você comprou: ${message.tile.left}|${message.tile.right}`);
        }
      }
    });

    // Turn changed
    this.gameWs.on('turnChanged', (message: ServerMessage) => {
      if (message.type === 'turnChanged') {
        if (message.playerId === this.playerId) {
          // DATE: 2025-12-20 | Commented out - too many "Sua vez!" messages polluting chat
          // The visual indicator in the status bar is sufficient
          // this.gameUI.addChatMessage('system', 'Sistema', 'Sua vez!');
          this.gameUI.playGameStartSound(); // Alert player it's their turn
        }
      }
    });

    // Game over
    this.gameWs.on('gameOver', (message: ServerMessage) => {
      if (message.type === 'gameOver') {
        console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        console.log(`🏆 [CLIENT] gameOver message received!`);
        console.log(`🏆 winnerId: ${message.winnerId}`);
        console.log(`🏆 reason: ${message.reason}`);
        console.log(`🏆 Current gameState:`, this.gameUI['gameState']);
        console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        this.gameUI.showGameOverModal(message.winnerId, message.reason);
      }
    });

    // DATE: 2025-11-06 | Author: IA + Luiz Couto
    // Task 3.2.1, 3.3.1: Round over - show modal with round results
    this.gameWs.on('roundOver', (message: any) => {
      if (message.type === 'roundOver') {
        console.log(`🏁 [2025-11-06] Round ${message.roundNumber} over. Goal reached: ${message.goalReached}`);
        this.gameUI.showRoundOverModal(
          message.roundNumber,
          message.winner,
          message.winnerTeam,
          message.pointsEarned,
          message.reason,
          message.currentScores,
          message.goalReached,
          message.nextRoundIn || 5
        );
      }
    });

    // DATE: 2025-11-06 | Author: IA + Luiz Couto
    // Task 3.4.1: Round starting - show countdown before next round
    this.gameWs.on('roundStarting', (message: any) => {
      if (message.type === 'roundStarting') {
        console.log(`▶️ [2025-11-06] Round ${message.roundNumber} starting. First player: ${message.firstPlayerId}, Countdown: ${message.countdown}s`);
        this.gameUI.showRoundStartingCountdown(message.roundNumber, message.countdown);
      }
    });

    // Error
    this.gameWs.on('error', (message: ServerMessage) => {
      if (message.type === 'error') {
        this.gameUI.showError(message.message);
        this.lobbyUI.showError(message.message);
      }
    });

    // Chat message
    this.gameWs.on('chat', (message: ServerMessage) => {
      if (message.type === 'chat') {
        this.gameUI.addChatMessage(message.playerId, message.playerName, message.message);
      }
    });

    // Disconnected
    this.gameWs.on('disconnected', () => {
      this.gameUI.addChatMessage('system', 'Sistema', 'Desconectado do servidor');
    });
  }

  /**
   * Setup WebSocket message handlers for lobby
   */
  private setupLobbyMessageHandlers(): void {
    // Lobby state received
    this.lobbyWs.on('lobbyState', (message: any) => {
      console.log('📥 Lobby state received:', message);
      if (message.type === 'lobbyState') {
        this.lobbyUI.updateLobbyState(message.state as ClientLobbyState);
      }
    });

    // Game created
    this.lobbyWs.on('gameCreated', (message: any) => {
      console.log('🎮 Game created:', message);
      if (message.type === 'gameCreated') {
        // Navigate to the game if redirect flag is set
        if (message.redirect) {
          this.connectToRoom(message.gameId);
        }
      }
    });

    // Game joined
    this.lobbyWs.on('gameJoined', (message: any) => {
      console.log('🎮 Game joined:', message);
      if (message.type === 'gameJoined') {
        this.connectToRoom(message.gameId, message.gameType);
      }
    });

    // Game updated
    this.lobbyWs.on('gameUpdated', (message: any) => {
      console.log('🔄 Game updated:', message);
      if (message.type === 'gameUpdated') {
        this.lobbyUI.updateGame(message.game);
      }
    });

    // Game removed
    this.lobbyWs.on('gameRemoved', (message: any) => {
      console.log('🗑️  Game removed:', message);
      if (message.type === 'gameRemoved') {
        this.lobbyUI.removeGame(message.gameId);
      }
    });

    // Queue update
    this.lobbyWs.on('queueUpdate', (message: any) => {
      console.log('⏳ Queue update:', message);
      if (message.type === 'queueUpdate') {
        this.lobbyUI.updateQueueStatus(message.position, message.estimatedWait);
      }
    });

    // Match found
    this.lobbyWs.on('matchFound', (message: any) => {
      console.log('✅ Match found:', message);
      if (message.type === 'matchFound') {
        this.lobbyUI.onQuickMatchFound(message.gameId, message.gameType);
      }
    });

    // Player joined lobby
    this.lobbyWs.on('playerJoinedLobby', (message: any) => {
      console.log('👋 Player joined lobby:', message);
    });

    // Player left lobby
    this.lobbyWs.on('playerLeftLobby', (message: any) => {
      console.log('👋 Player left lobby:', message);
    });

    // DATE: 2025-12-17 | Author: IA + Luiz Couto - Friend request notifications
    // Friend request received
    this.lobbyWs.on('friendRequestReceived', (message: any) => {
      console.log('🤝 Friend request received:', message);
      if (message.type === 'friendRequestReceived') {
        this.lobbyUI.handleFriendRequestReceived(
          message.fromPlayerId,
          message.fromPlayerName,
          message.fromNetcartasId,
          message.saidId,
          message.isAI
        );
      }
    });

    // Friend request accepted
    this.lobbyWs.on('friendAccepted', (message: any) => {
      console.log('🤝 Friend request accepted:', message);
      if (message.type === 'friendAccepted') {
        this.lobbyUI.handleFriendAccepted(
          message.playerId,
          message.playerName,
          message.netcartasId
        );
      }
    });

    // Friend request declined
    this.lobbyWs.on('friendDeclined', (message: any) => {
      console.log('🤝 Friend request declined:', message);
      if (message.type === 'friendDeclined') {
        this.lobbyUI.handleFriendDeclined(message.playerId);
      }
    });

    // Error
    this.lobbyWs.on('error', (message: any) => {
      console.error('❌ Lobby error:', message);
      if (message.type === 'error') {
        this.lobbyUI.showError(message.message);
      }
    });

    // Disconnected
    this.lobbyWs.on('disconnected', () => {
      console.log('🔌 Disconnected from lobby');
      this.lobbyConnected = false;
      this.lobbyUI.showError('Desconectado do lobby');
    });
  }

  /**
   * Create a new game room via lobby
   */
  private async createRoom(): Promise<void> {
    if (!this.lobbyConnected || !this.lobbyWs) {
      console.error('Not connected to lobby, cannot create room');
      this.lobbyUI.showError('Não conectado ao lobby');
      return;
    }

    try {
      console.log('🚀 Creating game via lobby');
      
      // Get game type from selector
      const gameTypeSelector = document.getElementById('game-type-selector') as HTMLSelectElement;
      const gameType = (gameTypeSelector?.value || 'partnerships_4p') as any;
      
      // Determine max players based on game type
      const maxPlayersMap: Record<string, number> = {
        'individual_2p': 2,
        'partnerships_4p': 4,
      };
      
      const maxPlayers = maxPlayersMap[gameType] || 4;
      
      // Create game configuration
      const config = {
        type: gameType,
        maxPlayers,
        isPrivate: false,
        allowSpectators: true,
        description: 'Mesa criada pelo jogador',
      };

      // Send create game message to lobby
      this.lobbyWs.send({ type: 'createGame', config } as any);
      console.log('📤 Sent createGame request to lobby');
      
    } catch (error) {
      console.error('❌ Error creating room:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      this.lobbyUI.showError(`Erro ao criar sala: ${errorMessage}`);
    }
  }

  /**
   * Connect to a game room via WebSocket
   *
   * DATE: 2025-10-28
   * DATE: 2025-12-26 | Added URL cleanup for deep link support
   * DATE: 2025-12-26 | Added aiNetcartasId parameter for correct AI companion selection
   * @param role - 'player' to join as player, 'spectator' to watch only
   * @param aiNetcartasId - Optional AI companion netcartas ID (server will use this to select correct AI)
   */
  private async connectToRoom(roomId: string, gameType?: string, role: 'player' | 'spectator' = 'player', aiNetcartasId?: number | string): Promise<void> {
    try {
      // Build WebSocket URL with game type and optional AI netcartas ID
      let wsUrl = `${SERVER_URL.replace('http', 'ws')}/api/rooms/${roomId}/ws`;
      const urlParams: string[] = [];

      if (gameType) {
        urlParams.push(`type=${gameType}`);
      }

      // DATE: 2025-12-26 | Include AI netcartas ID for proper AI companion selection
      if (aiNetcartasId) {
        urlParams.push(`ai=${aiNetcartasId}`);
        console.log(`🤖 [2025-12-26] Requesting specific AI companion: ${aiNetcartasId}`);
      }

      if (urlParams.length > 0) {
        wsUrl += `?${urlParams.join('&')}`;
      }

      console.log(`🔌 [2025-10-28] Connecting to game room as ${role}:`, wsUrl);

      await this.gameWs.connect(wsUrl);
      console.log('✅ Connected to game room successfully');

      // DATE: 2025-12-26 | Clean URL if it was a deep link (remove /game/xxx path)
      if (window.location.pathname.startsWith('/game/')) {
        console.log('🧹 [2025-12-26] Cleaning deep link URL');
        history.replaceState({}, '', '/');
      }
      
      // 2025-10-28: Set spectator mode
      this.isSpectator = role === 'spectator';
      
      // Store current room ID
      this.currentRoomId = roomId;
      
      // Switch to game screen
      this.showGameScreen();
      this.gameUI.updateRoomId(roomId);

      // DATE: 2025-12-17 | Author: IA + Luiz Couto
      // Pass player identity to GameUI for friend invite functionality
      if (this.userIdentity) {
        this.gameUI.setPlayerIdentity(
          this.userIdentity.netcartasId,
          this.userIdentity.nickname
        );
      }

      // 2025-10-28: Set spectator mode in UI
      if (this.isSpectator) {
        console.log('👁️ [2025-10-28] Entering spectator mode');
        this.gameUI.setSpectatorMode(true);
      }
      
    } catch (error) {
      console.error('❌ Error connecting to room:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`Erro ao conectar na sala. Detalhes: ${errorMessage}`);
      this.currentRoomId = null;
    }
  }

  /**
   * Show custom confirmation modal
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Displays a styled confirmation dialog and returns a promise
   * 
   * @param title - Modal title
   * @param message - Confirmation message
   * @returns Promise that resolves to true if confirmed, false if cancelled
   */
  private showConfirmation(title: string, message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const modal = document.getElementById('confirmation-modal');
      const titleEl = document.getElementById('confirmation-title');
      const messageEl = document.getElementById('confirmation-message');
      const okBtn = document.getElementById('confirmation-ok-btn');
      const cancelBtn = document.getElementById('confirmation-cancel-btn');

      if (!modal || !titleEl || !messageEl || !okBtn || !cancelBtn) {
        console.error('Confirmation modal elements not found');
        resolve(false);
        return;
      }

      // Set content
      titleEl.textContent = title;
      messageEl.textContent = message;

      // Show modal
      modal.classList.remove('hidden');

      // Handle confirmation
      const handleOk = () => {
        modal.classList.add('hidden');
        cleanup();
        resolve(true);
      };

      const handleCancel = () => {
        modal.classList.add('hidden');
        cleanup();
        resolve(false);
      };

      // Cleanup listeners
      const cleanup = () => {
        okBtn.removeEventListener('click', handleOk);
        cancelBtn.removeEventListener('click', handleCancel);
      };

      // Attach listeners
      okBtn.addEventListener('click', handleOk);
      cancelBtn.addEventListener('click', handleCancel);
    });
  }

  /**
   * Leave the current room and return to lobby
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * If player is actively playing (not spectator), shows confirmation dialog
   * Spectators can leave directly without confirmation
   */
  private async leaveRoom(): Promise<void> {
    // If user is a spectator, leave directly without confirmation
    if (this.isSpectator) {
      console.log('👁️ Spectator leaving room - no confirmation needed');
      this.disconnectFromGameRoom();
      return;
    }

    // If user is an active player, ask for confirmation
    const confirmLeave = await this.showConfirmation(
      'Você está no meio de uma partida!',
      'Ao sair agora, você estará abandonando o jogo e seus parceiros.\n\nTem certeza que deseja sair?'
    );

    if (confirmLeave) {
      console.log('🚪 Player confirmed leaving the game');
      this.disconnectFromGameRoom();
    } else {
      console.log('❌ Player cancelled leaving the game');
    }
  }

  /**
   * Disconnect from game room and return to lobby
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Date: 2025-12-15 | Update: Changed to full page refresh to avoid state contamination
   * Shared logic for leaving game room (used by both spectators and confirmed player exits)
   * Full page refresh ensures clean state when returning to lobby
   */
  private disconnectFromGameRoom(): void {
    console.log('🚪 Disconnecting from game room - performing full page refresh');

    // Disconnect WebSocket before refresh
    this.gameWs.disconnect();

    // Full page refresh to ensure clean state (no contamination from previous game)
    // This is more reliable than manually resetting all state variables
    window.location.reload();

    // ----- ORIGINAL CODE (commented out 2025-12-15) -----
    // Manual state reset approach - kept for reference if needed later
    // console.log('🚪 Disconnecting from game room WebSocket');
    //
    // // Disconnect WebSocket
    // this.gameWs.disconnect();
    //
    // // Reset GameUI state and DOM elements
    // this.gameUI.reset();
    //
    // // Reset main app state variables
    // this.currentRoomId = null;
    // this.playerId = null; // Reset player ID from game room
    // this.isSpectator = false; // Reset spectator flag
    //
    // console.log('✅ Game room disconnected and state reset complete');
    //
    // // Return to lobby screen
    // this.backToLobby();
    // ----- END ORIGINAL CODE -----
  }

  /**
   * Go back to lobby from game
   */
  private backToLobby(): void {
    this.lobbyUI.showLobbyScreen();
  }

  /**
   * Enter lobby from login screen
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * Extended to resolve UserIdentity (userId from URL or API) for friend system
   */
  private async enterLobby(): Promise<void> {
    const nameInput = document.getElementById('player-name') as HTMLInputElement;
    const playerName = nameInput.value.trim();

    if (!playerName) {
      alert('Por favor, insira seu nome');
      return;
    }

    // DATE: 2025-12-17 | Author: IA + Luiz Couto
    // Resolve UserIdentity - try URL param first, then API lookup
    await this.resolveUserIdentity(playerName);

    // DEBUG: Log final resolved identity
    console.log(`🔍 [2025-12-18 DEBUG] Final userIdentity after resolve:`, JSON.stringify(this.userIdentity));

    // Set player info
    const playerId = Date.now().toString();
    this.lobbyUI.setCurrentPlayer({
      id: playerId,
      name: playerName,
      gamesPlayed: 0,
      wins: 0,
      ranking: 0,
      // DATE: 2025-12-17 | Include netcartasId if available
      netcartasId: this.userIdentity?.netcartasId || undefined,
    });

    // Connect to lobby WebSocket and register player
    await this.connectToLobby(playerName);

    // Show lobby screen
    this.showLobbyScreen();

    // DATE: 2025-12-17 | Author: IA + Luiz Couto
    // Initialize PrivateChat component if user has a valid netcartasId
    // Users without accounts can't use private chat (need to create account first)
    if (this.userIdentity?.netcartasId) {
      console.log(`💬 [2025-12-17] Initializing PrivateChat for user ${playerName} (ID: ${this.userIdentity.netcartasId})`);
      this.privateChat.initialize(this.userIdentity.netcartasId, playerName);

      // DATE: 2025-12-22 | Initialize FriendRequestPopup for AI friend requests
      // This popup shows when an AI Companion sends a friend request during a game
      this.initializeFriendRequestPopup(this.userIdentity.netcartasId);

      // DATE: 2025-12-21 | Process deep link to open chat with AI Companion
      if (this.pendingOpenChat) {
        this.handleOpenChatDeepLink(this.pendingOpenChat);
        this.pendingOpenChat = null; // Clear after processing
      }
    } else {
      console.log(`ℹ️ [2025-12-17] PrivateChat not initialized - user not registered`);
    }

    // DATE: 2025-12-26 | Process game deep link to connect directly to a game room
    if (this.pendingGameDeepLink) {
      console.log(`🎮 [2025-12-26] Processing game deep link: ${this.pendingGameDeepLink.gameId}, aiNetcartasId: ${this.pendingGameDeepLink.aiNetcartasId || 'none'}`);
      // Small delay to ensure lobby is fully loaded
      setTimeout(() => {
        if (this.pendingGameDeepLink) {
          // Pass aiNetcartasId for proper AI companion selection
          this.connectToRoom(
            this.pendingGameDeepLink.gameId,
            this.pendingGameDeepLink.gameType,
            'player',
            this.pendingGameDeepLink.aiNetcartasId
          );
          this.pendingGameDeepLink = null; // Clear after processing
        }
      }, 500);
    }
  }

  /**
   * Initialize FriendRequestPopup and subscribe to ChatService events
   * DATE: 2025-12-22 | Author: IA + Luiz Couto
   *
   * Sets up the popup to show when an AI Companion sends a friend request.
   * The popup allows the user to accept, decline, or dismiss the request.
   */
  private initializeFriendRequestPopup(netcartasId: number): void {
    console.log(`🤝 [2025-12-22] Initializing FriendRequestPopup for user ${netcartasId}`);

    // Set user identity for API calls
    this.friendRequestPopup.setMyIdentity(netcartasId);

    // Set callbacks for popup actions
    this.friendRequestPopup.setCallbacks({
      onAccepted: (request) => {
        console.log(`✅ [2025-12-22] Friend request from ${request.fromNickname} accepted`);
        // The popup already called the API - ChatService will receive friendAdded event
      },
      onDeclined: (request) => {
        console.log(`❌ [2025-12-22] Friend request from ${request.fromNickname} declined`);
      },
      onDismissed: (request) => {
        console.log(`👋 [2025-12-22] Friend request from ${request.fromNickname} dismissed (pending)`);
      }
    });

    // Subscribe to friendRequestReceived events from ChatService
    const chatService = ChatService.getInstance();
    chatService.on('friendRequestReceived', (data: any) => {
      console.log(`🤝 [2025-12-22] Friend request received event:`, data);

      if (data.request) {
        // Show the popup with the friend request
        this.friendRequestPopup.show({
          saidId: data.request.saidId,
          fromNetcartasId: data.request.fromNetcartasId,
          fromNickname: data.request.fromNickname,
          isAI: data.request.isAI,
          requestedAt: data.request.requestedAt
        });
      }
    });
  }

  /**
   * Handle deep link to open chat with AI Companion
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   *
   * Called when user arrives via ?openChat=CompanionName from re-engagement email
   * Waits for ChatService to connect and load friends, then opens the chat window
   *
   * @param companionName - Name of the AI Companion to open chat with (e.g., "Lisa")
   */
  private async handleOpenChatDeepLink(companionName: string): Promise<void> {
    console.log(`🔗 [2025-12-21] Processing deep link: openChat=${companionName}`);

    // Wait for ChatService to connect and load friends list
    // This is necessary because friends are loaded asynchronously after WebSocket connects
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      const chatService = ChatService.getInstance();
      const friends = chatService.getFriends();

      console.log(`🔗 [2025-12-21] Looking for "${companionName}" in ${friends.length} friends`);

      // Find friend by name (case-insensitive)
      const friend = friends.find(f =>
        f.nickname.toLowerCase() === companionName.toLowerCase()
      );

      if (friend) {
        console.log(`✓ [2025-12-21] Found friend: ${friend.nickname} (ID: ${friend.netcartasId}, isAI: ${friend.isAI})`);
        // Open chat window with the companion
        this.privateChat.openChatWith(friend.netcartasId, friend.nickname, friend.isAI);
      } else {
        console.warn(`⚠️ [2025-12-21] Friend not found: ${companionName}`);
        console.log(`🔗 [2025-12-21] Available friends:`, friends.map(f => f.nickname));
      }
    } catch (error) {
      console.error(`❌ [2025-12-21] Error processing deep link:`, error);
    }
  }

  /**
   * Resolve UserIdentity from URL parameters or NetCartas API
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   *
   * Flow:
   * 1. Check URL param 'i' for userId
   * 2. If not found, call NetCartas API to lookup by nickname
   * 3. If still not found, user is not registered (netcartasId = null)
   *
   * @param nickname - Player's nickname
   */
  private async resolveUserIdentity(nickname: string): Promise<void> {
    console.log(`🔑 [2025-12-17] Resolving UserIdentity for: ${nickname}`);

    // Check if we already have auth data from URL
    const authData = getAuthFromUrlOrCookie();

    if (authData?.userId) {
      // UserId came from URL param 'i'
      console.log(`✅ [2025-12-17] UserId from URL: ${authData.userId}`);

      // DATE: 2025-12-21 | Still fetch user data from API to get profilePhoto
      try {
        const userData = await getUserByNickname(nickname);
        const profileImageURL = getNetCartasImageUrl(userData?.profilePhoto);
        this.userIdentity = {
          nickname: nickname,
          netcartasId: authData.userId,
          isRegistered: true,
          profileImageURL: profileImageURL
        };
        console.log(`✅ [2025-12-21] ProfilePhoto from API: ${profileImageURL || 'none'}`);
      } catch (error) {
        // Fallback without profilePhoto if API fails
        console.warn(`⚠️ [2025-12-21] Could not fetch profilePhoto:`, error);
        this.userIdentity = {
          nickname: nickname,
          netcartasId: authData.userId,
          isRegistered: true,
          profileImageURL: undefined
        };
      }
      return;
    }

    // No userId in URL, try to lookup via NetCartas API
    console.log(`🔍 [2025-12-17] UserId not in URL, searching via API...`);

    try {
      const userData = await getUserByNickname(nickname);

      if (userData) {
        console.log(`✅ [2025-12-17] User found via API: ${nickname} -> ID ${userData.id}`);
        const profileImageURL = getNetCartasImageUrl(userData.profilePhoto);
        this.userIdentity = {
          nickname: nickname,
          netcartasId: userData.id,
          isRegistered: true,
          profileImageURL: profileImageURL
        };
      } else {
        // User not found - not registered
        console.log(`ℹ️ [2025-12-17] User not registered: ${nickname}`);
        this.userIdentity = {
          nickname: nickname,
          netcartasId: null,
          isRegistered: false,
          profileImageURL: undefined
        };
      }
    } catch (error) {
      console.error(`❌ [2025-12-17] Error resolving UserIdentity:`, error);
      // On error, assume not registered
      this.userIdentity = {
        nickname: nickname,
        netcartasId: null,
        isRegistered: false,
        profileImageURL: undefined
      };
    }
  }

  /**
   * Connect to the lobby WebSocket
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * Extended to include netcartasId in joinLobby message for friend system
   */
  private async connectToLobby(playerName: string): Promise<void> {
    if (this.lobbyConnected) {
      console.log('Already connected to lobby');
      return;
    }

    try {
      const wsUrl = `${SERVER_URL.replace('http', 'ws')}/api/lobby/ws`;
      console.log('🏠 Connecting to lobby WebSocket:', wsUrl);

      await this.lobbyWs.connect(wsUrl);
      this.lobbyConnected = true;
      console.log('✅ Connected to lobby successfully');

      // Pass lobby WebSocket to LobbyUI
      this.lobbyUI.setLobbyWebSocket(this.lobbyWs);

      // DATE: 2025-12-18 | Author: IA + Luiz Couto
      // Pass callback to open private chat from lobby player profiles
      this.lobbyUI.setOpenChatCallback((netcartasId, nickname, isAI) => {
        console.log(`💬 [2025-12-18] Opening chat with ${nickname} (${netcartasId})`);
        this.privateChat.openChatWith(netcartasId, nickname, isAI);
      });

      // DATE: 2025-12-17 | Author: IA + Luiz Couto
      // Register player in lobby with their name AND netcartasId (for friend system)
      console.log('👋 Registering player in lobby:', playerName, 'netcartasId:', this.userIdentity?.netcartasId);
      this.lobbyWs.send({
        type: 'joinLobby',
        playerName,
        netcartasId: this.userIdentity?.netcartasId || null
      } as any);

      // 2025-10-25: Request lobby state to get all 200 pre-allocated game rooms
      console.log('🏠 [2025-10-25] Requesting lobby game rooms...');
      this.lobbyWs.send({ type: 'getLobbyState' } as any);

    } catch (error) {
      console.error('❌ Error connecting to lobby:', error);
      this.lobbyUI.showError('Erro ao conectar no lobby');
    }
  }

  /**
   * Leave lobby and return to login
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * Extended to disconnect PrivateChat when leaving lobby
   */
  private leaveLobby(): void {
    this.lobbyWs.disconnect();
    this.gameWs.disconnect();

    // DATE: 2025-12-17 | Disconnect private chat when leaving lobby
    if (this.privateChat.isInitialized()) {
      console.log(`💬 [2025-12-17] Disconnecting PrivateChat`);
      this.privateChat.disconnect();
    }

    this.currentRoomId = null;
    this.playerId = null;
    this.lobbyConnected = false;
    this.showLoginScreen();
  }

  /**
   * Show the login screen
   */
  private showLoginScreen(): void {
    document.getElementById('login-screen')?.classList.add('active');
    document.getElementById('game-screen')?.classList.remove('active');
    document.getElementById('lobby-screen')?.classList.remove('active');
  }

  /**
   * Show the lobby screen
   */
  private showLobbyScreen(): void {
    document.getElementById('login-screen')?.classList.remove('active');
    document.getElementById('game-screen')?.classList.remove('active');
    document.getElementById('lobby-screen')?.classList.add('active');
    
    // Play welcome sound when entering lobby
    this.lobbyUI.playWelcomeSound();
    
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Initialize admin panel if ?admin=true in URL
    this.lobbyUI.initializeAdminPanel();
  }

  /**
   * Show the game screen
   */
  private showGameScreen(): void {
    document.getElementById('login-screen')?.classList.remove('active');
    document.getElementById('lobby-screen')?.classList.remove('active');
    document.getElementById('game-screen')?.classList.add('active');
  }

  /**
   * DATE: 2025-12-14 | Author: IA + Luiz Couto
   * Auto-login with a provided username (from URL params or cookie)
   * Sets the username in the input field and triggers enterLobby
   * @param username - The username to use for auto-login
   */
  public autoLogin(username: string): void {
    console.info('[2025-12-14] Auto-login initiated for user:', username);

    // Set the username in the input field
    const nameInput = document.getElementById('player-name') as HTMLInputElement;
    if (nameInput) {
      nameInput.value = username;
    }

    // Clean URL parameters to remove sensitive data
    cleanUrlParameters();

    // Trigger the lobby entry
    this.enterLobby();
  }
}

// Initialize the game when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const game = new DominoGame();

  // DATE: 2025-12-26 | Check for game deep link with auth first
  const gameDeepLink = getGameDeepLinkParams();
  if (gameDeepLink?.username) {
    // Game deep link has auth - use it for auto-login
    console.info(`🎮 [2025-12-26] Game deep link auto-login: ${gameDeepLink.username}`);
    game.autoLogin(gameDeepLink.username);
    return;
  }

  // DATE: 2025-12-14 | Author: IA + Luiz Couto
  // Check for auto-login via URL parameters or cookies (NetCartas integration)
  const authData = getAuthFromUrlOrCookie();
  if (authData) {
    console.info('[2025-12-14] Auto-login triggered - entering lobby automatically');
    game.autoLogin(authData.username);
  } else {
    console.info('[2025-12-14] No auth data found - showing login screen');
  }
});
